// Which sidebar layout this device shows (#141): "chats" (one list of every chat across servers) or "classic" (one
// server's channels by category, with unread pills and banner). Remembered on this device only, like the other
// per-viewer choices (viewerPrefs.ts): the default is DEFAULT_CHAT_LAYOUT, and Settings → Appearance → Layout
// overrides it. Applied as <html data-layout>, which the sidebar CSS hangs on; Server.tsx reads it with useChatLayout()
// and switches in one place (sidebars.tsx). Changing it applies at once.
// Storage can be missing or throw (private windows, blocked site data): then the choice lasts until reload.
import { useSyncExternalStore } from "react";
import { CHAT_LAYOUTS, DEFAULT_CHAT_LAYOUT, type ChatLayout } from "@bloom/shared";

export const LAYOUT_KEY = "bloom:layout";

/** A stored value as a layout; anything else (missing, old, edited by hand) is the default. */
export function parseChatLayout(raw: unknown): ChatLayout {
  return CHAT_LAYOUTS.includes(raw as ChatLayout) ? (raw as ChatLayout) : DEFAULT_CHAT_LAYOUT;
}

function stored(): ChatLayout {
  try {
    return parseChatLayout(localStorage.getItem(LAYOUT_KEY));
  } catch {
    return DEFAULT_CHAT_LAYOUT;
  }
}

const listeners = new Set<() => void>();
let current: ChatLayout = stored();

function apply() {
  if (typeof document !== "undefined") document.documentElement.dataset.layout = current;
  for (const fn of listeners) fn();
}

export const getChatLayout = () => current;

export function setChatLayout(next: ChatLayout) {
  if (!CHAT_LAYOUTS.includes(next)) return;
  current = next;
  try {
    localStorage.setItem(LAYOUT_KEY, next);
  } catch {
    // private mode: still applies for this page
  }
  apply();
}

export function installChatLayout() {
  current = stored();
  apply();
  // Another tab changed it.
  addEventListener("storage", (e) => {
    if (e.key !== LAYOUT_KEY) return;
    current = stored();
    apply();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useChatLayout = (): ChatLayout => useSyncExternalStore(subscribe, () => current);
